
import { inject, Injectable, DOCUMENT } from '@angular/core';
import { CookieOptions } from '../../interfaces/cookie-options.interface';

@Injectable({
  providedIn: 'root',
})
export class CookieService {
  private readonly document = inject(DOCUMENT);

  /**
   * Checks if a cookie with the given name exists.
   * @param name The name of the cookie to check.
   * @returns True if the cookie exists, false otherwise.
   */
  checkIfCookieExists(name: string): boolean {
    return this.getCookie(name) !== null;
  }

  /**
   * Gets the value of the cookie with the given name.
   * @param name The name of the cookie to get.
   * @returns The value of the cookie, or null if the cookie does not exist.
   */
  getCookie(name: string): string | null {
    const match = this.document.cookie.match(
      RegExp('(?:^|;\\s*)' + name + '=([^;]*)'),
    );
    if (match && match.length > 1) return match[1];

    return null;
  }

  /**
   * Sets a cookie with a given name and value.
   * @param name The name of the cookie to set.
   * @param value The value of the cookie to set.
   * @param expiresOn The date and time when the cookie should expire. If not provided, the cookie will expire at the end of the current session.
   *
   **/
  setCookie(
    name: string,
    value: string,
    options: CookieOptions = {
      expiresOn: new Date(),
    },
  ): void {
    let formatStringCopy = `${name}=${value}; expires=${options.expiresOn?.toUTCString()};`;

    if (options.path) {
      formatStringCopy += `path=${options.path};`;
    }

    if (options.sameSite) {
      formatStringCopy += `sameSite=${options.sameSite};`;
    }

    if (options.secure) {
      formatStringCopy += 'secure;';
    }

    console.warn(formatStringCopy);

    this.document.cookie = formatStringCopy;
  }

  /**
   * Delete a cookie with a given name.
   *
   * @param name The name of the cookie to delete.
   */
  deleteCookie(name: string): void {
    const expiresOn = new Date();
    const lastYear = expiresOn.getFullYear() - 1;
    expiresOn.setFullYear(lastYear);
    this.setCookie(name, '', { expiresOn });
  }

  deleteCookieOnPath(name: string, path: string): void {
    const expiresOn = new Date();
    const lastYear = expiresOn.getFullYear() - 1;
    expiresOn.setFullYear(lastYear);
    this.setCookie(name, '', { expiresOn, path });
  }
}
